import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AuthConfig,
  AuthResult,
  DashboardHandoff,
  User,
} from "../shared/contracts";
import { ApiError, isExtension, request, saveSession } from "./api";
import { useConnectionStatus } from "./use-connection";
import {
  hostedCommand,
  hostedParentOrigin,
  isHostedSessionReset,
} from "./hosted-bridge";

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

async function webHandoff(
  signal: AbortSignal,
  embedded: boolean,
): Promise<{ code: string; verifier: string }> {
  const verifierBytes = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64Url(verifierBytes);
  const digest = await crypto.subtle.digest("SHA-256", verifierBytes);
  if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
  const challenge = base64Url(new Uint8Array(digest));
  if (embedded) {
    const handoff = await hostedCommand("connect", { challenge }, signal);
    return { code: handoff.code, verifier };
  }
  const requestId = crypto.randomUUID();
  const code = await new Promise<string>((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener("message", receive);
      signal.removeEventListener("abort", abort);
      window.clearTimeout(timeout);
    };
    const abort = () => {
      cleanup();
      reject(new DOMException("Cancelled", "AbortError"));
    };
    const receive = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin)
        return;
      const data = event.data;
      if (
        data?.source !== "411-agent-desk-extension" ||
        data?.type !== "connect-result" ||
        data?.requestId !== requestId
      )
        return;
      cleanup();
      if (!data.ok) {
        reject(
          new ApiError(
            data.status === 401
              ? "This GHL account is not linked for GHL Connect access. Ask an administrator to review the account."
              : typeof data.error === "string" && data.error
                ? data.error
                : "GHL connection failed.",
            typeof data.status === "number" ? data.status : 0,
          ),
        );
        return;
      }
      const handoff = data.data as DashboardHandoff | undefined;
      if (
        typeof handoff?.code !== "string" ||
        !handoff.code ||
        typeof handoff.expiresAt !== "number" ||
        handoff.expiresAt <= Date.now()
      ) {
        reject(new Error("GHL connection expired. Try again."));
        return;
      }
      resolve(handoff.code);
    };
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(
        new Error(
          "The GHL Connect extension did not respond. Install or configure it, then reload this dashboard and try again.",
        ),
      );
    }, 45_000);
    window.addEventListener("message", receive);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
    else
      window.postMessage(
        { source: "411-agent-desk-web", type: "connect", requestId, challenge },
        window.location.origin,
      );
  });
  return { code, verifier };
}

export function GhlSignIn({
  config,
  onSuccess,
  auto = false,
  surface = "dashboard",
  onFailure,
  onPendingChange,
}: {
  config: AuthConfig;
  onSuccess: (user: User) => void;
  auto?: boolean;
  surface?: "agent" | "dashboard";
  onFailure?: (error: Error & { status?: number }) => void;
  onPendingChange?: (pending: boolean) => void;
}) {
  const online = useConnectionStatus();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const attempted = useRef(false);
  const mounted = useRef(true);
  const controller = useRef<AbortController | null>(null);
  const automatic =
    auto &&
    config.ghlEnabled &&
    (isExtension || (surface === "agent" && Boolean(hostedParentOrigin)));
  // An eligible automatic attempt is pending before its effect starts. Keep
  // this separate from busy so the effect can still enter signIn.
  const pending = online && (busy || (automatic && !attempted.current));
  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);
  useEffect(() => {
    if (online) return;
    controller.current?.abort();
    controller.current = null;
    setBusy(false);
  }, [online]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (surface !== "agent" || !hostedParentOrigin) return;
    const reset = (event: MessageEvent) => {
      if (isHostedSessionReset(event)) controller.current?.abort();
    };
    window.addEventListener("message", reset);
    return () => window.removeEventListener("message", reset);
  }, [surface]);
  const signIn = useCallback(async () => {
    if (!config.ghlEnabled || !navigator.onLine || busy || controller.current)
      return;
    const attempt = new AbortController();
    controller.current = attempt;
    setBusy(true);
    setError("");
    const timeout = window.setTimeout(() => {
      if (!mounted.current || controller.current !== attempt) return;
      attempt.abort();
      controller.current = null;
      setBusy(false);
      const failure = new Error(
        "GHL connection did not finish. Try again to reconnect.",
      );
      setError(failure.message);
      onFailure?.(failure);
    }, 30_000);
    attempt.signal.addEventListener(
      "abort",
      () => window.clearTimeout(timeout),
      { once: true },
    );
    try {
      if (isExtension) {
        const response = await chrome.runtime.sendMessage({
          type: "desk:ghl-login",
        });
        if (!mounted.current || attempt.signal.aborted) return;
        if (!response?.ok)
          throw new ApiError(
            response?.status === 401
              ? "We could not connect this GHL account. Ask your administrator to check your pilot access and account link."
              : response?.error || "GHL sign-in is unavailable.",
            response?.status ?? 0,
          );
        const result = response.data as AuthResult | undefined;
        if (!result?.user)
          throw new Error("GHL sign-in did not return an account.");
        onSuccess(result.user);
      } else {
        const handoff = await webHandoff(
          attempt.signal,
          surface === "agent" && window.parent !== window,
        );
        if (attempt.signal.aborted) return;
        const result = await request<AuthResult>(
          "POST",
          surface === "agent"
            ? "/api/auth/agent-exchange"
            : "/api/auth/dashboard-exchange",
          handoff,
          surface,
        );
        if (!mounted.current || attempt.signal.aborted) return;
        if (!result?.token || !result?.user)
          throw new Error("GHL sign-in did not return an account.");
        saveSession(result.token, surface);
        onSuccess(result.user);
      }
    } catch (cause) {
      if (mounted.current && !attempt.signal.aborted) {
        const failure =
          cause instanceof Error
            ? cause
            : new Error("GHL sign-in is unavailable.");
        setError(failure.message);
        onFailure?.(failure);
      }
    } finally {
      window.clearTimeout(timeout);
      if (controller.current === attempt) controller.current = null;
      if (mounted.current && !attempt.signal.aborted) setBusy(false);
    }
  }, [busy, config.ghlEnabled, onSuccess, onFailure, surface]);
  useEffect(() => {
    if (!automatic || attempted.current || !online) return;
    attempted.current = true;
    void signIn();
  }, [automatic, online, signIn]);

  if (!config.ghlEnabled)
    return (
      <div className="agent-sso-state">
        <span
          className="agent-state-dot agent-state-dot-waiting"
          aria-hidden="true"
        />
        <strong>GHL connection is being set up</strong>
        <p>
          Your administrator needs to finish the GHL Connect Marketplace
          connection before GHL sign-in is available.
        </p>
      </div>
    );

  return (
    <div className="agent-sso-state" aria-busy={pending}>
      <span className="agent-state-dot" aria-hidden="true" />
      <strong>{pending ? "Connecting to GHL…" : "Use your GHL account"}</strong>
      <p>
        {!online
          ? "You are offline. Reconnect to continue with GHL."
          : pending
            ? "Checking your GHL account and team access…"
            : isExtension
              ? "Use the GHL tab where you are signed in. Your GHL Connect access will be checked automatically."
              : `Connect through the configured GHL Connect extension in this browser. Your ${surface === "agent" ? "quick tools" : "dashboard"} access will be checked.`}
      </p>
      <button
        type="button"
        disabled={pending || !online}
        onClick={() => void signIn()}
      >
        {pending ? "Connecting to GHL…" : "Continue with GHL"}
      </button>
      {error && (
        <p className="agent-inline-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
